import type { LayoutType } from '../layout-changer';
import type { CSSProperties } from 'react';

import { ChatIcon, Note, classNames, usePrevious } from '@movingimage-evp/mi-ui-component-library';
import { type TimedMetadataEvent } from '@movingimage-evp/player-library';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import {
  findEventTitle,
  findViewerEmailRegistrationPolicy,
  findViewerEmailWhitelistPolicy,
  findViewerSingleSignOnPolicy,
} from 'src/utils/graphql-helpers';
import { parseJwt } from 'src/utils/parse-jwt';
import { getSessionStorageItem } from 'src/utils/session-storage';

import { useMediaQuery } from '../../../../../common/media-query-provider';
import { useChat } from '../../../../../components/chat/use-sendbird-chat';
import { useDevTools } from '../../../../../dev-tools';
import {
  type GetWebcastStreamQuery,
  type GetWebcastViewerQuery,
  State,
  StreamState,
  useGetWebcastStreamLazyQuery,
} from '../../../../../generated/graphql-viewer';
import { getUserInformation } from '../../../../../utils/user-information-utils';
import { Content } from '../../../../components/content';
import { type SlideCuepoint, SlidePreview } from '../../../../components/slide-preview';
import { ViewerChat } from '../../../../components/viewer-chat';
import { useSelectedLanguage } from '../../../../providers/selected-language-provider';
import { useViewerAccess } from '../../../../providers/viewer-access-provider';
import { ViewerHeader } from '../../components/header/header';
import styles from '../../stream.module.css';
import { DraggableArea } from '../draggable-area';
import { ViewerPlayer } from '../viewer-player';

type Props = {
  webcast: GetWebcastStreamQuery['webcast'] & GetWebcastViewerQuery['webcast'];
};

export function LiveView({ webcast }: Props) {
  const { webcastId = '' } = useParams();
  const { t } = useTranslation();
  const { selectedLanguage, primaryLanguage, isCustomLanguage } = useSelectedLanguage();
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const { messages, connectUser } = useChat();
  const { email, accessCode, protectionToken } = useViewerAccess();
  const { mockedViewerChat, mockedViewerChatNamesVisible, mockedViewerControls } = useDevTools();
  const { isMobile } = useMediaQuery();

  const [slideCuepoint, setSlideCuepoint] = useState<SlideCuepoint | undefined>(undefined);
  const [layoutType, setLayoutType] = useState<LayoutType>('sideBySide');
  const [layoutSwapped, setLayoutSwapped] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [liveTestNotificationVisible, setLiveTestNotificationVisible] = useState(webcast?.state === State.LIVE_TEST);
  const [newMessageCounter, setNewMessages] = useState(0);
  const [draggableStyle, setDraggableStyle] = useState<CSSProperties>();
  const [playerOverlayHidden, setPlayerOverlayHidden] = useState(false);
  const userAccessToken = getSessionStorageItem('viewerAccessToken');
  const [employeeId, setEmployeeId] = useState<string>();

  const language = isCustomLanguage(selectedLanguage?.code) ? primaryLanguage?.code : selectedLanguage?.code;
  const streams = webcast?.streaming?.live?.streams;
  const currentStream = streams?.find((stream) => stream.metadata?.language === selectedLanguage?.code) || streams?.[0];
  const primaryStreamConnected = currentStream?.primaryStatus?.streamState === StreamState.CONNECTED;
  const backupStreamConnected = currentStream?.backupStatus?.streamState === StreamState.CONNECTED;

  const title = findEventTitle(webcast?.contents, language, webcast?.primaryLang.code);
  const accountId = webcast?.accountId;
  const lsproId = webcast?.lsproId;
  const primaryColor = webcast?.appearance?.primaryColor;
  const iconColor = webcast?.appearance?.iconColor;

  const logoUrl = webcast?.appearance?.logoUrl || undefined;
  const horizontalLogoUrl = webcast?.appearance?.horizontalLogoUrl || undefined;
  const inIframe = window.location !== window.parent.location;
  const webcastCurrentSlide = webcast?.currentSlide;
  const slideVisible = (webcastCurrentSlide || slideCuepoint) !== undefined;

  const chatType = mockedViewerChat || webcast?.chat?.type;
  const chatExists = chatType !== undefined;
  const useProvidedUsername = mockedViewerChatNamesVisible ?? webcast?.chat?.useProvidedUsername;

  const [chatVisible, setChatVisible] = useState(!isMobile && !inIframe && chatExists);

  const singleSignOnPolicy = findViewerSingleSignOnPolicy(webcast?.securityPolicies);
  const emailRegistrationPolicy = findViewerEmailRegistrationPolicy(webcast?.securityPolicies);
  const emailWhitelistPolicy = findViewerEmailWhitelistPolicy(webcast?.securityPolicies);
  const hasSecurityPolicy = Boolean(singleSignOnPolicy || emailRegistrationPolicy || emailWhitelistPolicy);

  const isShowWatermarkingAvailable = Boolean(singleSignOnPolicy?.showWatermarking);

  useEffect(() => {
    if (!isMobile && chatExists) setChatVisible(true);
  }, [chatExists, isMobile]);

  const chatButtonVisible = chatExists && !chatVisible && !fullscreen;
  const previousMessagesLength = usePrevious(messages.length);

  useEffect(() => {
    const messageLength = messages.length;

    if (previousMessagesLength < messageLength && !chatVisible) {
      setNewMessages(newMessageCounter + 1);
    }
  }, [messages.length, previousMessagesLength, setNewMessages, newMessageCounter, chatVisible]);

  const connectToChatManually = (nickname?: string) => {
    if (!webcast?.chat) return;

    connectUser({
      userId: getUserInformation(webcastId)?.userId || Date.now().toString(),
      nickname,
      channelUrl: webcast?.chat?.channelUrl,
      applicationId: webcast?.chat?.chatProviderAccountId,
      eventId: webcastId,
      apiHost: webcast?.chat?.apiHost,
      wsHost: webcast?.chat?.wsHost,
    });
  };

  const handleOpenChat = () => {
    setChatVisible(true);
    setNewMessages(0);
  };

  const onTimedMetadata = useCallback(
    (event: TimedMetadataEvent) => {
      if (event.metadata.length > 0) {
        try {
          setSlideCuepoint(JSON.parse(event.metadata[0].value));
        } catch {
          return;
        }
      }
    },
    [setSlideCuepoint]
  );

  const [getWebcastStream] = useGetWebcastStreamLazyQuery();

  const onPlayerRetry = useCallback(() => {
    getWebcastStream({ variables: { webcastId, accessCode, email, protectionToken }, fetchPolicy: 'network-only' });
  }, [accessCode, email, getWebcastStream, protectionToken, webcastId]);

  useEffect(() => {
    document.addEventListener('fullscreenchange', () => setFullscreen(Boolean(document.fullscreenElement)));
  }, []);

  useEffect(() => {
    setSlideCuepoint(mockedViewerControls.mockedSlide);
  }, [mockedViewerControls.mockedSlide]);

  useEffect(() => {
    if (isShowWatermarkingAvailable && userAccessToken) {
      const userInfoJwt = parseJwt(userAccessToken);
      if (userInfoJwt?.employeeId) setEmployeeId(userInfoJwt.employeeId);
    }
  }, [isShowWatermarkingAvailable, userAccessToken]);

  return (
    <div
      data-testid="live-view"
      className={classNames(
        styles.wrapper,
        styles.webcastIsLive,
        chatVisible && styles.chatVisible,
        slideVisible && styles.slideVisible,
        inIframe && styles.inIframe
      )}
    >
      <main
        id="viewer-main"
        data-testid="viewer-main"
        className={classNames(styles.main, fullscreen && styles.fullscreen)}
      >
        <ViewerHeader
          chatButtonVisible={chatButtonVisible}
          fullscreen={fullscreen}
          fullScreenButtonVisible={slideVisible}
          layoutType={layoutType}
          onOpenChat={handleOpenChat}
          onLayoutSwapped={() => setLayoutSwapped(!layoutSwapped)}
          onSetLayoutType={setLayoutType}
          slideVisible={slideVisible}
          logoUrl={horizontalLogoUrl || logoUrl}
          chatType={chatType}
          languageSwitchHidden={playerOverlayHidden}
          inIframe={inIframe}
        />

        <section>
          <div
            ref={videoWrapperRef}
            data-testid="video-wrapper"
            className={classNames(
              styles.videoWrapper,
              slideVisible && layoutSwapped && styles.layoutSwapped,
              slideVisible && styles[layoutType]
            )}
          >
            <DraggableArea
              data-testid="player-draggable-area"
              enabled={layoutType === 'pictureInPicture' && !layoutSwapped}
              containerRef={videoWrapperRef}
              className={styles.draggable}
              style={draggableStyle}
              onStop={setDraggableStyle}
            >
              {primaryStreamConnected || backupStreamConnected ? (
                <ViewerPlayer
                  className={styles.player}
                  currentStream={currentStream}
                  accountId={accountId}
                  logoUrl={logoUrl || horizontalLogoUrl}
                  lsproId={lsproId}
                  primaryColor={primaryColor}
                  iconColor={iconColor}
                  title={title}
                  webcastId={webcastId}
                  employeeId={employeeId}
                  autoplay
                  hidePlaybackSpeed
                  onTimedMetadata={onTimedMetadata}
                  onPlayerRetry={onPlayerRetry}
                  onOverlayVisibilityChange={setPlayerOverlayHidden}
                />
              ) : (
                <div data-testid="waiting-for-stream-info" className={styles.placeholder}>
                  {t('viewer.views.stream.waitingForStream')}
                </div>
              )}
            </DraggableArea>

            <DraggableArea
              data-testid="slide-draggable-area"
              enabled={layoutType === 'pictureInPicture' && layoutSwapped}
              className={styles.draggable}
              style={draggableStyle}
              containerRef={videoWrapperRef}
              onStop={setDraggableStyle}
            >
              <SlidePreview className={styles.slidePreview} slideCuepoint={slideCuepoint} />
            </DraggableArea>
          </div>

          {!inIframe && <Content className={styles.content} webcast={webcast} language={selectedLanguage?.code} />}
        </section>
      </main>

      {chatButtonVisible && (
        <button data-testid="mobile-chat-button" className={styles.mobileChatButton} onClick={handleOpenChat}>
          {t(`viewer.views.stream.chat.buttons.open.label.${chatType}`)}
          {newMessageCounter > 0 ? <span>{newMessageCounter}</span> : <ChatIcon />}
        </button>
      )}

      {chatVisible && (
        <ViewerChat
          chatType={chatType}
          hasSecurityPolicy={hasSecurityPolicy}
          useProvidedUsername={useProvidedUsername}
          onNicknameSubmit={connectToChatManually}
          inIframe={inIframe}
          onClose={() => setChatVisible(false)}
        />
      )}

      {liveTestNotificationVisible && (
        <Note
          testId="live-test-notification"
          className={styles.liveTestNotification}
          onClose={() => setLiveTestNotificationVisible(false)}
        >
          {t('viewer.views.stream.liveTestNotification')}
        </Note>
      )}
    </div>
  );
}
